import getSlugFromLabel from 'commons/getSlugFromLabel';

// Cache for slug transformations to avoid repeated computations
const slugCache = new Map();

const getSlugFromLabelCached = label => {
  if (!slugCache.has(label)) {
    slugCache.set(label, getSlugFromLabel(label));
  }
  return slugCache.get(label);
};

// Pre-compute product attribute maps for faster lookups
const createProductAttributeMap = attributes => {
  const attributeMap = new Map();

  attributes?.forEach(attribute => {
    const { id: attributeId, values: attributeValues } = attribute;
    const transformedValues = attributeValues.map(getSlugFromLabelCached);
    attributeMap.set(attributeId, new Set(transformedValues));
  });

  return attributeMap;
};

/**
 * Optimized function to check if a product should be shown based on list filters.
 * Uses O(1) lookups with Map/Set instead of O(n) array operations for better performance.
 */
const getIsProductShownForListFilters = ({ activeListFilters, attributes }) => {
  // Early return if no active filters
  if (!activeListFilters || Object.keys(activeListFilters).length === 0) {
    return true;
  }

  // Create a map of product attributes for O(1) lookups
  const productAttributeMap = createProductAttributeMap(attributes);

  // Check each active filter
  for (const [activeFilterId, activeFilterOptions] of Object.entries(
    activeListFilters
  )) {
    if (activeFilterOptions?.length) {
      // Get product attributes for this filter (O(1) lookup)
      const productAttributeSet = productAttributeMap.get(activeFilterId);

      if (!productAttributeSet) {
        // Product doesn't have this attribute, so it doesn't match
        return false;
      }

      // Check if any active filter options match product attributes
      // Using Set.has() for O(1) lookups instead of array intersection
      const hasMatchingOption = activeFilterOptions.some(option =>
        productAttributeSet.has(option)
      );

      if (!hasMatchingOption) {
        return false;
      }
    }
  }

  return true;
};

export default getIsProductShownForListFilters;
